import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import orderAPI from "../../services/omsapi";

const staticStatusMap = {
    1: "Pending",
    2: "Confirmed",
    3: "Admin Cancelled",
    4: "Packed",
    5: "Shipped",
    6: "Delivered",
    7: "Customer Cancelled",
};

const OrderStatusUpdateForm = ({ orderId: propOrderId }) => {
    const { register, handleSubmit, control, setValue, watch, formState: { errors }, reset } = useForm();
    const [orderDetails, setOrderDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchId, setSearchId] = useState("");
    const [orderId, setOrderId] = useState(propOrderId || null);
    const [statusMap, setStatusMap] = useState(staticStatusMap);
    const [searching, setSearching] = useState(false);

    useEffect(() => {
        if (orderId) {
            fetchOrder(orderId);
        }

        orderAPI.getAllOrdersStatus()
            .then((res) => {
                if (res?.OrderStatusData) {
                    setStatusMap(res.OrderStatusData);
                }
            })
            .catch(() => {});
    }, [orderId]);

    const fetchOrder = (id) => {
        orderAPI.getOrderById(id)
            .then((res) => {
                setSearching(false);
                const data = Array.isArray(res.data) ? res.data[0] : res.data;
                if (!data) throw new Error("Order not found");

                setOrderDetails(data);

                setValue('newStatus', {
                    value: data.order_status,
                    label: statusMap[data.order_status] || "Unknown",
                });
            })
            .catch(() => {
                setSearching(false);
                toast.error(`Order ID ${id} not found`);
                setOrderDetails(null);
            });
    };

    const handleSearch = () => {
        const trimmed = searchId.trim();
        if (!trimmed) {
            toast.warning("Please enter an Order ID to search.");
            return;
        }

        setSearching(true);
        setOrderId(trimmed);
    };

    const onSubmit = (data) => {
        if (!orderId || !orderDetails) {
            toast.error("No order selected.");
            return;
        }

        if (data.newStatus.value === orderDetails.order_status) {
            toast.warning("Status is the same as current status.");
            return;
        }

        setLoading(true);

        const payload = {
            orderId: orderDetails.id, // internal DB ID
            status: data.newStatus.value,
            reason: data.reason || "",
        };

        orderAPI.updateOrderStatus(payload)
            .then((response) => {
                const status = response.status || 'FAIL';
                if(status==='FAIL'){
                    toast.error(`Update failed: ${response?.message || 'Something wrong try again later'}`);
                    return false;
                }
                toast.success("Order status updated successfully!");
                reset();
                setSearchId("");
                setOrderDetails(null);
                setOrderId(null);
            })
            .catch((err) => {
                toast.error(`Update failed: ${err.response?.data?.message || err.message}`);
            })
            .finally(() => setLoading(false));
    };

    const statusOptions = Object.entries(statusMap).map(([key, label]) => ({
        value: Number(key),
        label,
    }));

    return (
        <div className="container mt-5 mb-5">
            <ToastContainer />
            <h3>Update Order Status</h3>

            {!propOrderId && (
                <div className="mb-3 row">
                    <div className="col-sm-8">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Order ID or Show ID"
                            value={searchId}
                            onChange={(e) => setSearchId(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleSearch();
                                }
                            }}
                        />
                    </div>
                    <div className="col-sm-4">
                        <button
                            onClick={handleSearch}
                            className="btn btn-secondary w-100"
                            disabled={searching || !searchId.trim()}
                        >
                            {searching ? "Searching..." : "Search Order"}
                        </button>
                    </div>
                </div>
            )}

            {orderDetails && (
                <>
                    <div className="row mb-4">
                        <div className="col-md-6">
                            <div className="alert alert-info">
                                <strong>Order ID:</strong> {orderDetails.id}<br />
                                <strong>Show ID:</strong> {orderDetails.order_showid}<br />
                                <strong>Branch:</strong> {orderDetails.branch_name}<br />
                                <strong>Current Status:</strong> {statusMap[orderDetails.order_status] || "Unknown"}<br /><br />
                                <strong>Customer:</strong> {orderDetails.customer_name}<br />
                                <strong>Mobile:</strong> {orderDetails.customer_mobile}<br />
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="alert alert-secondary">
                                <strong>Shipping Address:</strong><br />
                                {orderDetails.shipping_name}<br />
                                {orderDetails.shipping_address}<br />
                                {orderDetails.shipping_landmark && <>Landmark: {orderDetails.shipping_landmark}<br /></>}
                                {orderDetails.shipping_area}, {orderDetails.shipping_city} - {orderDetails.shipping_pincode}<br />
                                {orderDetails.shipping_state}
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="mb-3">
                            <label className="form-label">Change Status</label>
                            <Controller
                                name="newStatus"
                                control={control}
                                rules={{ required: "Please select a status" }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={statusOptions}
                                        placeholder="Select new status"
                                    />
                                )}
                            />
                            {errors.newStatus && <p className="text-danger">{errors.newStatus.message}</p>}
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Reason (Optional)</label>
                            <textarea
                                {...register("reason")}
                                className="form-control"
                                rows={3}
                            />
                        </div>

                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? 'Updating...' : 'Update Status'}
                        </button>
                    </form>
                </>
            )}
        </div>
    );
};

export default OrderStatusUpdateForm;
